import React, { useState } from 'react';
import { X, Upload, Download, CheckCircle, AlertCircle, FileText, RefreshCw } from 'lucide-react';
import { assetsAPI } from '../services/api';

const BulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Instructions, 2: Upload & Preview, 3: Confirm, 4: Result
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        alert('❌ File harus format CSV!');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await assetsAPI.downloadImportTemplate();
      alert('✅ Template berhasil didownload!');
    } catch (error) {
      alert('❌ Gagal download template: ' + error.message);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      alert('Pilih file CSV terlebih dahulu');
      return;
    }

    setLoading(true);
    try {
      const response = await assetsAPI.previewImport(file);
      setPreview(response.data);
      setStep(3);
    } catch (error) {
      alert('❌ Gagal preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await assetsAPI.confirmImport(file);
      setImportResult(response.data);
      setStep(4);
      if (onSuccess) onSuccess();
    } catch (error) {
      alert('❌ Gagal import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setImportResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">📥 Bulk Import Assets</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Step {step} of 4: {step === 1 && 'Instructions'}{step === 2 && 'Upload File'}{step === 3 && 'Preview & Validate'}{step === 4 && 'Import Complete'}
            </p>
          </div>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Instructions */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="glass-card p-6 rounded-lg border border-primary/30 bg-primary/5">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <span>Cara Import Assets Secara Bulk</span>
                </h3>
                <ol className="space-y-3 text-sm text-foreground">
                  <li className="flex items-start space-x-2">
                    <span className="font-bold text-primary">1.</span>
                    <span><strong>Download Template CSV</strong> dengan klik button di bawah</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-bold text-primary">2.</span>
                    <span><strong>Buka file di Excel/Google Sheets</strong> dan isi data assets Anda</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-bold text-primary">3.</span>
                    <span><strong>Hapus 2 example rows</strong> sebelum upload (Dell & Lenovo)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-bold text-primary">4.</span>
                    <span><strong>Save as CSV</strong> (bukan .xlsx atau format lain)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-bold text-primary">5.</span>
                    <span><strong>Upload file</strong> untuk preview dan validasi</span>
                  </li>
                </ol>
              </div>

              <div className="glass-card p-6 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                <h4 className="font-semibold text-yellow-600 mb-3">⚠️ Penting:</h4>
                <ul className="space-y-2 text-sm text-foreground">
                  <li>• Fields dengan tanda <code className="bg-muted px-1 rounded">*</code> adalah <strong>wajib diisi</strong></li>
                  <li>• Format tanggal: <code className="bg-muted px-1 rounded">YYYY-MM-DD</code> (contoh: 2025-01-15)</li>
                  <li>• Category: laptop, desktop, printer, server, networking, atau accessories</li>
                  <li>• Harga dalam angka tanpa separator (contoh: 15000000)</li>
                  <li>• Barcode dan Serial Number harus unik (tidak boleh duplicate)</li>
                  <li>• Maximum 500 rows per upload</li>
                </ul>
              </div>

              <div className="flex justify-between pt-4">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center space-x-2"
                >
                  <Download className="w-5 h-5" />
                  <span>Download Template CSV</span>
                </button>
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Next: Upload File →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Upload File */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="glass-card p-8 rounded-lg border-2 border-dashed border-border text-center">
                <Upload className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Upload CSV File</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Pilih file CSV yang sudah Anda isi dengan data assets
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  {file ? '✓ File Selected: ' + file.name : 'Choose CSV File'}
                </label>
              </div>

              {file && (
                <div className="glass-card p-4 rounded-lg border border-green-500/30 bg-green-500/5">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-600">File ready for preview</p>
                      <p className="text-sm text-muted-foreground">{file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={handlePreview}
                  disabled={!file || loading}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Validating...</span>
                    </>
                  ) : (
                    <span>Preview & Validate →</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Validate */}
          {step === 3 && preview && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="glass-card p-4 rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground mb-1">Total Rows</p>
                  <p className="text-2xl font-bold text-foreground">{preview.total_rows}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-green-500/30 bg-green-500/5">
                  <p className="text-sm text-green-600 mb-1">✓ Valid Rows</p>
                  <p className="text-2xl font-bold text-green-600">{preview.valid_rows}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-red-500/30 bg-red-500/5">
                  <p className="text-sm text-red-600 mb-1">✗ Invalid Rows</p>
                  <p className="text-2xl font-bold text-red-600">{preview.invalid_rows}</p>
                </div>
              </div>

              {/* Errors */}
              {preview.errors && preview.errors.length > 0 && (
                <div className="glass-card p-6 rounded-lg border border-red-500/30 bg-red-500/5 max-h-64 overflow-y-auto">
                  <h4 className="font-semibold text-red-600 mb-3 flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5" />
                    <span>Errors Found ({preview.errors.length})</span>
                  </h4>
                  <div className="space-y-3">
                    {preview.errors.map((error, index) => (
                      <div key={index} className="text-sm">
                        <p className="font-medium text-red-600">Row {error.row}:</p>
                        <ul className="list-disc list-inside text-foreground ml-4">
                          {error.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview Data */}
              {preview.preview_data && preview.preview_data.length > 0 && (
                <div className="glass-card p-6 rounded-lg border border-border">
                  <h4 className="font-semibold text-foreground mb-3">Preview Valid Data (First 10 rows)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3">Barcode</th>
                          <th className="text-left py-2 px-3">Brand</th>
                          <th className="text-left py-2 px-3">Model</th>
                          <th className="text-left py-2 px-3">Category</th>
                          <th className="text-right py-2 px-3">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview_data.map((row, index) => (
                          <tr key={index} className="border-b border-border/50">
                            <td className="py-2 px-3 font-mono text-xs">{row.barcode}</td>
                            <td className="py-2 px-3">{row.brand}</td>
                            <td className="py-2 px-3">{row.model}</td>
                            <td className="py-2 px-3">{row.category}</td>
                            <td className="py-2 px-3 text-right">Rp {row.purchase_price.toLocaleString('id-ID')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={preview.valid_rows === 0 || loading}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Confirm Import ({preview.valid_rows} rows)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && importResult && (
            <div className="space-y-6">
              <div className="glass-card p-8 rounded-lg border border-green-500/30 bg-green-500/5 text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-600 mb-2">Import Complete!</h3>
                <p className="text-lg text-foreground mb-4">
                  Successfully imported <span className="font-bold">{importResult.imported}</span> assets
                </p>
                {importResult.failed > 0 && (
                  <p className="text-sm text-red-600">
                    {importResult.failed} rows failed to import
                  </p>
                )}
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="glass-card p-6 rounded-lg border border-red-500/30 bg-red-500/5">
                  <h4 className="font-semibold text-red-600 mb-3">Failed Rows:</h4>
                  <ul className="text-sm space-y-1 text-foreground">
                    {importResult.errors.map((err, index) => (
                      <li key={index}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center pt-4">
                <button
                  onClick={handleClose}
                  className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Close & Refresh Assets
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;